import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import useWindowSize from '../../hooks/useWindowSize';
import NavBar, { NAV_BAR_HEIGHT } from '../NavBar/NavBar';
import NavMenu from '../NavMenu/NavMenu';

const SIDEBAR_WIDTH = 244;
const SIDEBAR_COLLAPSED_WIDTH = 60;
const MOBILE_BREAKPOINT = 900;
const COLLAPSED_KEY = 'nav_collapsed';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${(props) => props.theme.bodyBackground};
`;

const SidebarContainer = styled.aside<{
  $width: number;
  $mobile: boolean;
  $open: boolean;
}>`
  position: ${(props) => (props.$mobile ? 'fixed' : 'sticky')};
  top: 0;
  left: 0;
  z-index: ${(props) => (props.$mobile ? 1300 : 10)};
  width: ${(props) => props.$width}px;
  height: 100vh;
  flex-shrink: 0;
  background-color: ${(props) => props.theme.navBg};
  border-right: 1px solid ${(props) => props.theme.border};
  transition: width 0.18s ease, transform 0.2s ease;
  transform: ${(props) =>
    props.$mobile && !props.$open ? 'translateX(-100%)' : 'none'};
  box-shadow: ${(props) =>
    props.$mobile && props.$open ? props.theme.shadow.lg : 'none'};
`;

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1250;
  background: ${(props) => props.theme.overlayBg};
`;

// --shell-top and --page-pad-y let full-height pages (maps, chat) size
// themselves against the viewport without knowing about the shell.
const MainContent = styled.main<{ $mobile: boolean }>`
  --shell-top: ${(props) => (props.$mobile ? NAV_BAR_HEIGHT : 0)}px;
  --page-pad-y: ${(props) => (props.$mobile ? 16 : 24)}px;
  --page-pad-x: ${(props) => (props.$mobile ? 16 : 28)}px;

  flex: 1;
  min-width: 0;
  margin-top: var(--shell-top);
  padding: var(--page-pad-y) var(--page-pad-x);
`;

interface LayoutProps {
  children: React.ReactNode;
}

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

const Layout = ({ children }: LayoutProps) => {
  const windowSize = useWindowSize();
  const location = useLocation();
  const isMobile = windowSize[0] > 0 && windowSize[0] < MOBILE_BREAKPOINT;
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      const next = !value;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Storage can be unavailable (private mode), the toggle still works.
      }
      return next;
    });
  };

  const railMode = collapsed && !isMobile;

  return (
    <LayoutContainer>
      {isMobile && <NavBar onMenuClick={() => setMobileOpen(true)} />}
      {isMobile && mobileOpen && <Scrim onClick={() => setMobileOpen(false)} />}
      <SidebarContainer
        $width={railMode ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}
        $mobile={isMobile}
        $open={mobileOpen}
      >
        <NavMenu
          collapsed={railMode}
          onToggleCollapsed={isMobile ? undefined : toggleCollapsed}
          onNavigate={() => setMobileOpen(false)}
        />
      </SidebarContainer>
      <MainContent $mobile={isMobile}>{children}</MainContent>
    </LayoutContainer>
  );
};

export default Layout;
